import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheBoatDbApi } from '../../providers/useTheBoatDbApi';
import type { Endpoints, Schemas } from '../generated/theboatdb-typed-openapi';
import { useApiKeysQueryKey } from './useApiKeysQuery';

export type CreatedApiKey = Schemas.CreatedApiKey;

/** What a caller may send. `environment` is accepted by the API but is the deployment's to decide. */
export type CreateApiKeyInput = Endpoints.post_CreateApiKey['parameters']['body'];

/**
 * Mints a key.
 *
 * The response is the only one that ever carries the plaintext value, and it
 * must not end up in the query cache — so the list is *invalidated and refetched*
 * rather than patched with what came back. The plaintext lives only in whatever
 * component holds the returned value, for as long as its dialog is open.
 *
 * `mutate({ name })` rather than `mutate({ body: { name } })`: the generated
 * mutation takes the endpoint's parameter object, which is the right shape for
 * the transport and an odd one at a call site that has a form's values in hand.
 */
export const useCreateApiKeyMutation = () => {
  const { tanstackApiClient } = useTheBoatDbApi();
  const queryClient = useQueryClient();
  const keysQueryKey = useApiKeysQueryKey();

  // Only the function and the key are taken from the generated options. The rest
  // — `onMutate`, `onSettled` and friends — are typed against the *endpoint's*
  // parameter object, so spreading them alongside a `mutationFn` that takes a
  // plain body makes the two disagree about what a mutation's variables are.
  const { mutationFn, mutationKey } = tanstackApiClient.mutation('post', '/v1/keys', {
    selectFn: (response: Schemas.CreatedApiKeyResponse) => response.data,
  }).mutationOptions;

  return useMutation({
    mutationKey,
    mutationFn: async (input: CreateApiKeyInput) => mutationFn({ body: input }),
    // Not retried at all, at any count: this is the one operation here that is
    // irreversible and billable, and a retry that succeeds after a response we
    // failed to read would mint a second key the user never asked for.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keysQueryKey });
    },
  });
};
